import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import AuthRedirectHandler from "./components/auth/AuthRedirectHandler";
import CompetitionDetails from "./pages/competition/CompetitionDetails";
import CompetitionList from "./pages/competition/CompetitionList";
import FreeSubscriptionsPage from "./pages/free-subscriptions/FreeSubscriptionPage";
import StripeSubscriptionPage from "./pages/free-subscriptions/StripeSubscriptionPage";
import LoginPage from "./pages/auth/login/LoginPage";
import Sidebar from "./components/layout/Sidebar";
import UsersList from "./pages/user/UserList";
import UsersPage from "./pages/users/UsersPage";
import WhatsNewPage from "./pages/whatsnew/WhatsNewPage";
import GearPage from "./pages/gear/GearPage";
import AdvertisementPage from "./pages/advertisements/AdvertisementPage";
import LivePage from "./pages/live/LivePage";
import SpotImagesPage from "./pages/spotimages/SpotImagesPage";
import AdminLogPage from "./pages/admin/AdminLogPage";
import SystemStatusPage from "./pages/system/SystemStatusPage";
import WindProvidersPage from "./pages/admin/WindProvidersPage";
import WindAlertsPage from "./pages/admin/WindAlertsPage";
import WindStationsPage from "./pages/admin/WindStationsPage";
import PushStationsPage from "./pages/admin/PushStationsPage";
import CampaignsPage from "./pages/campaigns/CampaignsPage";
import CampaignDetailPage from "./pages/campaigns/CampaignDetailPage";
import WelcomePage from "./pages/welcome/WelcomePage";
import TestDataPage from "./pages/admin/TestDataPage";
import { IS_TEST_ENV } from "./constants/api";

// Code-split the chat page — pulls in react-markdown + highlight.js (~250kB)
// only for admins who actually open it.
const ChatPage = lazy(() => import("./pages/chat/ChatPage"));

/**
 * Route guard for the root path. Logged-out users land on /login;
 * logged-in admins see the WelcomePage (rendered as a Sidebar child below).
 * Non-admin authenticated users have no access to the console at all.
 */
const RootRoute = () => {
    const { user, initialized } = useAuth();
    if (!initialized) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (!user.isadmin) return <Navigate to="/login" replace />;
    return <WelcomePage />;
};

function RoutesComponent() {
    return (
        <>
            <AuthRedirectHandler />
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route element={<Sidebar />}>
                    <Route path="/" element={<RootRoute />} />
                    <Route path="/live" element={<LivePage />} />
                    <Route path="/free-subscriptions" element={<FreeSubscriptionsPage />} />
                    <Route path="/stripe-subscriptions" element={<StripeSubscriptionPage />} />
                    <Route path="/competitions" element={<CompetitionList />} />
                    <Route path="/competitions/details" element={<CompetitionDetails />} />
                    <Route path="/competitions/users" element={<UsersList />} />
                    <Route path="/competitions/devices" element={<UsersList initialTab="devices" />} />
                    <Route path="/users" element={<UsersPage />} />
                    <Route path="/whatsnew" element={<WhatsNewPage />} />
                    <Route path="/gear" element={<GearPage />} />
                    <Route path="/advertisements" element={<AdvertisementPage />} />
                    <Route path="/spot-images" element={<SpotImagesPage />} />
                    <Route path="/admin-log" element={<AdminLogPage />} />
                    <Route path="/system-status" element={<SystemStatusPage />} />
                    <Route path="/wind-providers" element={<WindProvidersPage />} />
                    <Route path="/wind-alerts" element={<WindAlertsPage />} />
                    <Route path="/wind-stations" element={<WindStationsPage />} />
                    <Route path="/push-stations" element={<PushStationsPage />} />
                    <Route path="/campaigns" element={<CampaignsPage />} />
                    <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
                    {/* Test-only surface: route exists only in local/test builds;
                        the backend proxy additionally 404s outside STAGE=TEST. */}
                    {IS_TEST_ENV && <Route path="/test-data" element={<TestDataPage />} />}
                    <Route
                        path="/chat"
                        element={
                            <Suspense fallback={<div className="p-8 text-sm text-gray-500">Loading chat…</div>}>
                                <ChatPage />
                            </Suspense>
                        }
                    />
                </Route>
            </Routes>
        </>
    );
}

export default RoutesComponent;
